import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSiteName } from '../contexts/SiteSettingsContext';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { Heart, ShoppingCart, Menu, User, LogOut, Package, LayoutDashboard, Leaf } from 'lucide-react';
import BrandName from './BrandName';

const Header = () => {
  const { user, logout } = useAuth();
  const siteName = useSiteName();
  const navigate = useNavigate();
  const brandStyle = {
    fontFamily: "'Proxima Nova Wide', 'Proxima Nova', 'Avenir Next', 'Helvetica Neue', Arial, sans-serif",
    fontWeight: 600,
    fontStretch: 'expanded',
    letterSpacing: '0.02em',
    fontStyle: 'normal',
  };
  const brandColor = '#6F1F45';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#FAFAFA]/70 border-b border-[#E0E0E0]/50" data-testid="main-header">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 md:mr-12" data-testid="logo-link">
          <Leaf className="h-5 w-5 text-primary" />
          <span className="text-[28px] font-semibold tracking-wide" style={{ ...brandStyle, color: brandColor }}>
            {siteName.includes('\u2122') ? <BrandName /> : siteName}
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8 flex-1">
          <Link to="/" className="text-sm font-medium text-[#2E7D32] hover:text-[#1A237E] transition-colors leading-none" data-testid="nav-home">Home</Link>
          <Link to="/products" className="text-sm font-medium text-[#2E7D32] hover:text-[#1A237E] transition-colors leading-none" data-testid="nav-shop">Shop</Link>
          <Link to="/subscription" className="text-sm font-medium text-[#2E7D32] hover:text-[#1A237E] transition-colors leading-none" data-testid="nav-subscription">Ritual Subscription</Link>
          <Link to="/blogs" className="text-sm font-medium text-[#2E7D32] hover:text-[#1A237E] transition-colors leading-none" data-testid="nav-blogs">Blogs</Link>
          <Link to="/bulk-purchase" className="text-sm font-medium text-[#2E7D32] hover:text-[#1A237E] transition-colors leading-none" data-testid="nav-bulk-purchase">Bulk Purchase</Link>
        </nav>

        {/* Right icons */}
        <div className="flex items-center space-x-2">
          {user ? (
            <>
              <Link to="/wishlist" data-testid="wishlist-icon" className="p-2 rounded-full hover:bg-black/5 transition-colors">
                <Heart className="h-[18px] w-[18px] text-[#1A237E]/60 hover:text-primary transition-colors" />
              </Link>
              <Link to="/cart" data-testid="cart-icon" className="p-2 rounded-full hover:bg-black/5 transition-colors">
                <ShoppingCart className="h-[18px] w-[18px] text-[#1A237E]/60 hover:text-primary transition-colors" />
              </Link>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="user-menu-trigger">
                    <User className="h-[18px] w-[18px]" />
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <div className="mt-8 space-y-4">
                    <p className="font-heading font-semibold text-lg" data-testid="user-name">{user.name}</p>
                    <p className="text-sm text-muted-foreground" data-testid="user-email">{user.email}</p>
                    <div className="space-y-1 pt-2">
                      <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => navigate('/profile')} data-testid="profile-link">
                        <User className="mr-2 h-4 w-4" /> Profile
                      </Button>
                      <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => navigate('/orders')} data-testid="orders-link">
                        <Package className="mr-2 h-4 w-4" /> My Orders
                      </Button>
                      {user.role === 'admin' && (
                        <Button variant="ghost" className="w-full justify-start text-sm" onClick={() => navigate('/admin')} data-testid="admin-dashboard-link">
                          <LayoutDashboard className="mr-2 h-4 w-4" /> Admin Dashboard
                        </Button>
                      )}
                      <Button variant="ghost" className="w-full justify-start text-sm text-destructive" onClick={handleLogout} data-testid="logout-button">
                        <LogOut className="mr-2 h-4 w-4" /> Logout
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-button" className="hidden md:inline-flex text-sm font-medium text-[#2E7D32]">Login</Button>
              <Button variant="outline" onClick={() => navigate('/guest-checkout')} data-testid="guest-checkout-button" className="hidden md:inline-flex rounded-full text-sm font-medium">Guest Checkout</Button>
              <Button onClick={() => navigate('/register')} className="hidden md:inline-flex rounded-full text-sm font-medium" data-testid="register-button">Get Started</Button>
            </>
          )}

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="h-11 w-11" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <nav className="flex flex-col space-y-5 mt-10">
                <Link to="/" className="font-heading text-xl" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-home">Home</Link>
                <Link to="/products" className="font-heading text-xl" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-shop">Shop</Link>
                <Link to="/subscription" className="font-heading text-xl" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-subscription">Ritual Subscription</Link>
                <Link to="/blogs" className="font-heading text-xl" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-blogs">Blogs</Link>
                <Link to="/bulk-purchase" className="font-heading text-xl" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-bulk-purchase">Bulk Purchase</Link>
                {!user && (
                  <div className="pt-4 border-t border-[#E0E0E0] space-y-3" data-testid="mobile-auth-buttons">
                    <Button variant="outline" className="w-full rounded-full py-3 min-h-[44px] text-base" onClick={() => { setMobileMenuOpen(false); navigate('/login'); }} data-testid="mobile-login-button">Login</Button>
                    <Button variant="outline" className="w-full rounded-full py-3 min-h-[44px] text-base" onClick={() => { setMobileMenuOpen(false); navigate('/guest-checkout'); }} data-testid="mobile-guest-checkout-button">Guest Checkout</Button>
                    <Button className="w-full rounded-full py-3 min-h-[44px] text-base" onClick={() => { setMobileMenuOpen(false); navigate('/register'); }} data-testid="mobile-register-button">Get Started</Button>
                  </div>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
